import { Box, Stack } from "@mui/material";
import { useErrorHandler } from "@smartrr/shared/components/ErrorBoundary";
import { InfiniteScroll } from "@smartrr/shared/components/InfiniteScroll";
import { ItemSkeleton } from "@smartrr/shared/components/InfiniteScroll/components/ItemSkeleton";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { SMARTRR_CUSTOM_ATTRIBUTES_VALUE } from "@smartrr/shared/constants";
import { ConfirmCancelButtons } from "@smartrr/shared/customerPortal/components/ConfirmCancelButtons";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { TitleAndSearchBar } from "@smartrr/shared/customerPortal/components/TitleAndSearchBar";
import { IPurchasableVariant } from "@smartrr/shared/entities/PurchasableVariant";
import { ISmartrrBundlePacks } from "@smartrr/shared/entities/SellingPlanGroup";
import {
  ICustomAttributesItems,
  ISmartrrInfoCustomAttributes,
  getBundlePackByShopifyVariantId,
} from "@smartrr/shared/utils/bundles";
import { shopifyGidToNumber, viewShopifyId } from "@smartrr/shared/utils/ensureShopifyGid";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import {
  useVariantShopifyIDToPurchasableMap,
  useVariantToPurchasableMap,
} from "@smartrr/shared/utils/useVariantToPurchasableMap";
import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { useSmartrrTheme } from "@customer-app/account/views/OperationAquarius/hooks/useSmartrrTheme";
import { typedCustomerFrontendApi } from "@customer-app/shared/typedCustomerFrontendApi";
import { updateBundleCustomAttributes, updateBundleLine } from "@customer-app/state/actionCreators/bundles";
import { updateSubscriptionLineItem } from "@customer-app/state/actionCreators/customerPurchaseStates";
import { useCloseModal } from "@customer-app/state/actionCreators/modal";
import {
  useProductsListForSellingPlanId,
  useVariantListForSellingPlanId,
} from "@customer-app/state/reducers/sellingPlanGroups";
import { useSmartrrCustomerDispatch } from "@customer-app/state/typedCustomerReduxHooks";

import { BundleBuilderItem } from "./libs/components/BundleBuilderItem";
import { PackSelection } from "./libs/components/PackSelection";
import { SelectedBundleItemsRow } from "./libs/components/SelectedBundleItemsRow";
import { IEditBundleContentModal, IEditBundleModal } from "./libs/types";
import {
  filterVariantsByHiddenProducts,
  filterVariantsBySearchText,
  useFilteredVariantsByCollectionContents,
} from "./libs/utils";
import { useMobileQuery } from "../../../../../../utils";
import { useAsyncReference } from "../../../../../hooks/useAsyncReference";
import { useCurrentBundle } from "../../../../../hooks/useCurrentBundle";
import { NoProducts } from "../../../AddonList/libs/components/NoProducts";
import { VerticalProductWrapper } from "../../../constants";
import { useBasicSubscriptionInfo } from "../../ManageSubscriptionsModal/hooks/useBasicSubscriptionInfo";
import { DeliveryAccess } from "@customer-app/state/stores/delivery";

export const EditBundleModal = ({
  deliveryId,
  bundleParentItem,
  returnToModal,
}: IEditBundleModal): JSX.Element => {
  const delivery = DeliveryAccess.useDelivery(deliveryId);

  if (!delivery) {
    return <React.Fragment />;
  }

  return (
    <EditBundleContentModal
      delivery={delivery}
      bundleParentItem={bundleParentItem}
      returnToModal={returnToModal}
    />
  );
};

const EditBundleContentModal = ({
  delivery,
  bundleParentItem,
  returnToModal,
}: IEditBundleContentModal): JSX.Element => {
  const dispatch = useSmartrrCustomerDispatch();
  const closeModal = useCloseModal();
  const handleError = useErrorHandler();
  const theme = useSmartrrTheme();
  const { purchaseState, sellingPlanId } = useBasicSubscriptionInfo(delivery);

  if (!sellingPlanId) {
    handleError(new Error("Delivery missing selling plan id"));
  }

  const { bundle } = useCurrentBundle(delivery);

  const subscribablePurchasables = useProductsListForSellingPlanId(sellingPlanId, true);

  const validVariantsFromSubscribablePurchasables = useVariantListForSellingPlanId(sellingPlanId, delivery);

  const [subscribedPurchasablesSearchText, updateSubscribedPurchasablesSearchText] = useState<string>("");
  const [variantsToShow, setVariantsToShow] = useState<IPurchasableVariant[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const activeQty = useRef<number | null>(null);

  const isMobile = useMobileQuery();

  const [activePack, setActivePack] = useState<ISmartrrBundlePacks | undefined>(
    getBundlePackByShopifyVariantId(bundle!, bundleParentItem)!
  );
  const maxProductQuantity = activePack?.maxProducts ?? 0;
  const [cartQuantity, setCartQuantity] = useAsyncReference(0);
  const stackRef = useRef<HTMLDivElement>(null);

  // Assigning ref variables to prevent stale-state
  const [cart, setCart] = useState<ICustomAttributesItems[] | undefined>();
  const cartRef = useRef(cart);
  const [remainingQuantity, setRemainingQuantity] = useState<number>(maxProductQuantity - cartQuantity.current);

  useEffect(() => {
    cartRef.current = cart;
    setRemainingQuantity(maxProductQuantity - cartQuantity.current);
  }, [cart, remainingQuantity, cartQuantity, maxProductQuantity]);

  let filteredSubscribedVariants = validVariantsFromSubscribablePurchasables;
  const variantToPurchasableMap = useVariantToPurchasableMap(subscribablePurchasables);
  const variantShopifyIdToPurchasableMap = useVariantShopifyIDToPurchasableMap(subscribablePurchasables);

  /**
   * Filter variants based on inputted search text - filters by variant and purchasable names
   */
  filteredSubscribedVariants = filterVariantsBySearchText(
    filteredSubscribedVariants,
    subscribedPurchasablesSearchText,
    validVariantsFromSubscribablePurchasables,
    variantToPurchasableMap
  );

  /**
   * Filter out all purchasables that are hidden through liquid callback
   */
  filteredSubscribedVariants = filterVariantsByHiddenProducts(
    filteredSubscribedVariants,
    variantToPurchasableMap
  );

  /**
   * Filter out all valid purchasables that are not in the Active pack's collection.
   */
  filteredSubscribedVariants = useFilteredVariantsByCollectionContents(
    activePack,
    filteredSubscribedVariants,
    variantToPurchasableMap
  );

  const clearCart = useCallback(() => setCart(undefined), []);

  const updateCart = useCallback(
    (variantId: string | number, quantity: number) => {
      if (sellingPlanId == null) {
        handleError(new Error("sellingPlanId is not defined."));
        return;
      }

      variantId = viewShopifyId(variantId);

      const parsedSellingPlanId = viewShopifyId(sellingPlanId);

      if (!cartRef.current) {
        // If cart is empty, initialize
        setCart([{ id: variantId, quantity, selling_plan: parsedSellingPlanId }]);
        return;
      }

      const isItemNew = !cartRef.current?.some(item => item.id === variantId);

      if (quantity === 0) {
        // if quantity is 0, remove any instances
        setCart(cart => cart?.filter(item => item.id !== variantId));
      } else if (isItemNew) {
        // If variant doesn't exist already in cart, add
        if (cartQuantity.current + quantity > maxProductQuantity) {
          toast.error(typedLocalize("editBundle.maxItemsErrorToast"));
          return;
        }
        setCart(cart => [...cart!, { id: variantId, quantity, selling_plan: parsedSellingPlanId }]);
      } else {
        // If variants exists, update
        const cartStateUpdate = cartRef.current?.map(item => {
          if (item.id === variantId) {
            // if quantity in cart is greater than new quantity, we're decreasing quantity
            if (
              item.quantity < quantity &&
              cartQuantity.current - item.quantity + quantity > maxProductQuantity
            ) {
              toast.error(typedLocalize("editBundle.maxItemsErrorToast"));
              return item;
            }
            return { ...item, quantity };
          }
          return item;
        });

        setCart(cartStateUpdate);
      }
      // setActivePurchaseable(null);
      (activeQty as MutableRefObject<number | null>).current = 0;
    },
    [sellingPlanId, cart, cartRef, cartQuantity, maxProductQuantity]
  );

  const onConfirmHandler = useCallback(
    async (cart: ICustomAttributesItems[]) => {
      if (cartQuantity.current !== maxProductQuantity) {
        toast(
          typedLocalize("editBundle.missingItemsToast", {
            num: maxProductQuantity - cartQuantity.current,
          })
        );
        return;
      }
      setIsLoading(true);
      const smartrrCustomAttributes = bundleParentItem?.customAttributes?.find(
        ele => ele.key === SMARTRR_CUSTOM_ATTRIBUTES_VALUE
      )?.value;
      const smartrrCustomAttributesParsed: ISmartrrInfoCustomAttributes = JSON.parse(smartrrCustomAttributes!);

      const originalItemIds = smartrrCustomAttributesParsed?.items?.map(item => item.id);

      const originalTitles = await Promise.all(
        originalItemIds?.map(async id => {
          const purchasable = await typedCustomerFrontendApi.getReq(`/purchasable-variant/${id}`);

          if (purchasable.type === "error") {
            return;
          }

          return purchasable.body.purchasableName;
        })
      );

      const updatedBundleLines = cart.map(item => {
        return { id: +item.id, quantity: item.quantity };
      });
      smartrrCustomAttributesParsed.items = cart.map(item => {
        return { id: item.id, quantity: item.quantity };
      });

      const updatedTitlesAndQuantities = await Promise.all(
        updatedBundleLines.map(async lines => {
          const purchasable = await typedCustomerFrontendApi.getReq(`/purchasable-variant/${lines.id}`);
          if (purchasable.type === "error") {
            return;
          }
          return { title: purchasable.body.purchasableName, quantity: lines.quantity };
        })
      );

      const updatedAttributes: any = {
        [SMARTRR_CUSTOM_ATTRIBUTES_VALUE]: JSON.stringify(smartrrCustomAttributesParsed),
      };

      if (updatedTitlesAndQuantities) {
        for (const item of updatedTitlesAndQuantities) {
          updatedAttributes[item?.title] = "" + item?.quantity;
        }
      }

      await typedCustomerFrontendApi.putReq(`/purchase-state/:id/customAttributes/remove`, {
        params: {
          id: purchaseState.id,
        },
        reqBody: {
          attributeKeys: await originalTitles,
          lineItemShopifyIds: [bundleParentItem?.shopifyId!],
        },
      });

      // Updating bundle lines
      await dispatch(updateBundleLine(updatedBundleLines, bundleParentItem.id)).then(res => {
        if (res.type === "ERROR_UPDATING_BUNDLE_LINES") {
          toast.error(typedLocalize("editBundle.errorUpdatingBundleToast"));
        }
      });

      // Updating custom attributes
      await dispatch(
        updateBundleCustomAttributes(updatedAttributes, purchaseState.id, bundleParentItem?.shopifyId!)
      ).then(res => {
        if (res.type === "ERROR_UPDATING_BUNDLE_CUSTOM_ATTRIBUTES") {
          toast.error(typedLocalize("editBundle.errorUpdatingBundleToast"));
        }
      });

      // Setting new pack / variant if changing old one
      if (viewShopifyId(bundleParentItem.vnt.shopifyId) !== activePack?.buyWithVariant) {
        // using pack's variant id to locate associated purchaseable, then locating matching variant
        const newBundlePurchasable = variantShopifyIdToPurchasableMap[activePack?.buyWithVariant ?? ""];
        const newBundleParentVariant = newBundlePurchasable?.vnts?.find(
          vnt => viewShopifyId(vnt.shopifyId) === activePack?.buyWithVariant
        );

        if (newBundleParentVariant) {
          await dispatch(
            updateSubscriptionLineItem(
              purchaseState.id,
              bundleParentItem.id,
              {
                variantId: newBundleParentVariant?.id!,
              },
              toast
            )
          );
        } else {
          handleError(new Error("Error locating pack variant"));
        }
      }
      //TODO: Reformat to be within a try-catch
      // Finally
      toast(typedLocalize("editBundle.bundleUpdatedToast"));
      setIsLoading(false);
      location.reload();
      closeModal();
    },
    [
      cartQuantity,
      maxProductQuantity,
      bundleParentItem?.customAttributes,
      bundleParentItem.id,
      bundleParentItem?.shopifyId,
      bundleParentItem.vnt.shopifyId,
      purchaseState.id,
      activePack?.buyWithVariant,
      variantShopifyIdToPurchasableMap,
      toast,
    ]
  );

  // Adding Values to bundle from bundleParentItem
  useEffect(() => {
    if (!cart && bundleParentItem.customAttributes) {
      const cartUpdate: ICustomAttributesItems[] = [];

      bundleParentItem?.bundleLines?.map((item: ICustomAttributesItems) => {
        if (cartUpdate.length) {
          for (const x in cartUpdate) {
            if (cartUpdate[x].id === item.id) {
              // If item already exists in array, update qty
              cartUpdate[x].quantity += item.quantity;
              return;
            }
          }
        }
        cartUpdate.push({ ...item });
      });
      setCart(cartUpdate);
    }
    setRemainingQuantity(0);
  }, []);

  useEffect(() => {
    if (cart?.length === 0) {
      setCartQuantity(0);
    }

    if (activePack) {
      setVariantsToShow(filteredSubscribedVariants);
    } else {
      setCart(undefined);
      setCartQuantity(0);
    }

    let tempQty = 0;
    cart?.length && cart.forEach((item: ICustomAttributesItems) => (tempQty += item.quantity));
    setCartQuantity(tempQty);

    setRemainingQuantity(maxProductQuantity - cartQuantity.current);
  }, [activePack, cart, cartQuantity, maxProductQuantity]);

  useEffect(() => {
    setVariantsToShow(filteredSubscribedVariants.slice(0, 10));
  }, [subscribedPurchasablesSearchText]);

  return (
    <React.Fragment>
      {bundle ? <PackSelection activePack={activePack} bundle={bundle} setActivePack={setActivePack} /> : null}
      {isLoading ? (
        <Stack sx={{ height: "600px" }} direction="column" justifyContent="center" alignItems="center">
          <DotSpinner />
        </Stack>
      ) : (
        <Stack ref={stackRef} spacing={"20px"} pt={"20px"}>
          {activePack ? (
            <Box>
              <Box className={prefixClassName("bundle-add-items")}>
                <TitleAndSearchBar
                  title={typedLocalize("editBundle.addItemsTitle", {
                    minNum: maxProductQuantity,
                  })}
                  isMobile={isMobile}
                  subscribedPurchasablesSearchText={subscribedPurchasablesSearchText}
                  updateSubscribedPurchasablesSearchText={updateSubscribedPurchasablesSearchText}
                />
              </Box>
              {filteredSubscribedVariants.length > 0 ? (
                <Box
                  sx={{
                    overflowX: "scroll",
                    height: "max-content",
                    overflowY: "hidden",
                    marginTop: "15px",
                    marginRight: "-20px",
                    minHeight: "214px",
                    transition: "0.3s ease-in-out",
                  }}
                >
                  <InfiniteScroll
                    isParentLoading={isLoading}
                    hasMore={filteredSubscribedVariants.length > variantsToShow.length}
                    skeleton={<ItemSkeleton radius={theme.modernImageBorderRadius} />}
                    horizontal={true}
                    handleAddItemsToScroll={numberOfItemsToLoad =>
                      setVariantsToShow(prevItems => [
                        ...prevItems,
                        ...filteredSubscribedVariants.slice(variantsToShow.length, numberOfItemsToLoad),
                      ])
                    }
                  >
                    {variantsToShow.map(vnt => {
                      if (!vnt.shopifyId) {
                        return <React.Fragment />;
                      }

                      const variantShopifyId = String(shopifyGidToNumber(vnt.shopifyId));
                      const defaultItemState = {
                        id: variantShopifyId,
                        quantity: 0,
                        selling_plan: sellingPlanId,
                      };

                      // if item exists in cart, use cart attributes
                      // if item is 'new', use default attributes
                      const item: ICustomAttributesItems =
                        cartRef.current?.find(item => String(item.id) === variantShopifyId) ?? defaultItemState;

                      return (
                        <VerticalProductWrapper item key={vnt.id}>
                          <BundleBuilderItem
                            key={item.id}
                            cartRef={cartRef}
                            cartQuantityRef={cartQuantity}
                            item={item}
                            maxProductQuantity={maxProductQuantity}
                            updateCart={updateCart}
                            useAddButton
                          />
                        </VerticalProductWrapper>
                      );
                    })}
                  </InfiniteScroll>
                </Box>
              ) : (
                <NoProducts height="217px !important" />
              )}
            </Box>
          ) : null}
          {activePack ? (
            <SelectedBundleItemsRow
              cartQuantityRef={cartQuantity}
              cartRef={cartRef}
              isLoading={isLoading}
              maxProductQuantity={maxProductQuantity}
              updateCart={updateCart}
              clearCart={clearCart}
              remainingQuantity={remainingQuantity}
            />
          ) : null}
          <ConfirmCancelButtons
            isLoading={isLoading}
            onCancel={() => returnToModal()}
            onConfirm={() => onConfirmHandler(cart!)}
            cancelTextOverride={"general.backToMangeOrderBtn"}
          />
        </Stack>
      )}
    </React.Fragment>
  );
};
